import type { FC, ReactElement } from "react"

import { LazyMotion, domAnimation, motion, useReducedMotion } from "framer-motion"
import { Outlet } from "react-router-dom"

import { AssistantHeader, ETypographyColor, HelpHint } from "@/3514/components"
import { useDefaultStylesReset, useHtmlScrollbarGutter } from "@/3514/hooks"
import { buildCssClassName } from "@/3514/utils"

const LAYOUT_CLASS_NAME: string = "a-layout"

const classes = {
    main: (): string =>
        buildCssClassName(
            "relative w-full flex flex-1 flex-col items-center [min-height:100dvh] pb-[50px]",
            LAYOUT_CLASS_NAME
        ),
    outletContainer: (): string => "w-full flex-1 flex flex-col gap-y-[20px] md:gap-y-[50px] items-center",
    outletWrapper: (): string => "w-full flex-1 flex flex-col"
} as const

const Layout: FC = (): ReactElement => {
    useHtmlScrollbarGutter({ isEnabled: true })

    useDefaultStylesReset(LAYOUT_CLASS_NAME)

    const isMotionReduced: boolean = useReducedMotion()

    return (
        <main className={classes.main()}>
            <AssistantHeader />

            <div className={classes.outletContainer()}>
                <div className={classes.outletWrapper()}>
                    <LazyMotion features={domAnimation} key="al-lm">
                        <Outlet />
                    </LazyMotion>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{
                        duration: isMotionReduced ? 0 : 0.3,
                        delay: 0.2,
                        type: "tween",
                        ease: "easeInOut"
                    }}
                >
                    <footer>
                        <HelpHint linkColor={ETypographyColor.Blue200} />
                    </footer>
                </motion.div>
            </div>
        </main>
    )
}

Layout.displayName = "AssistantLayout"

export { Layout as AssistantLayout }
